import React from 'react';
import Typography from '@material-ui/core/Typography';
import { Section, Panel, MetricGrid, DataTable, LearningHubBadge, Callout } from './shared';
import { formatCalendarDate, formatCount, formatDate, formatPercent } from '../utils';
import { useInsightsStyles } from '../styles';

/** "Days left" cell: scheduled plans count down to their start, never "0". */
export const daysLeftText = row => {
  if (row.access_state === 'scheduled') {
    const days = Number(row.days_until_start);
    if (days === 1) return 'Starts tomorrow';
    if (days > 1) return `Starts in ${formatCount(days)} days`;
    return `Starts ${formatCalendarDate(row.access_start_date)}`;
  }
  if (row.access_state === 'expired') return 'Expired';
  return formatCount(row.days_left);
};

const waitingLine = row => {
  const count = Number(row.learners_in_project);
  return `${formatCount(count)} ${count === 1 ? 'learner' : 'learners'} in ${row.project} ${count === 1 ? 'gets' : 'get'} access when the plan starts on ${formatCalendarDate(row.access_start_date)}.`;
};

const testEnrollmentsHint = count => `+${formatCount(count)} test ${Number(count) === 1 ? 'enrolment' : 'enrolments'} not counted`;

/** Info banner for learners waiting on scheduled plans; otherwise the "nobody enrolled" warning. */
const LearningHubBanner = ({ plans, projects, totals }) => {
  if (Number(plans.learners_waiting_for_start) > 0) {
    const waiting = projects.filter(row => row.access_state === 'scheduled' && Number(row.learners_in_project) > 0);
    if (!waiting.length) return null;
    return (
      <div style={{ marginTop: 16 }} data-testid="learning-hub-waiting">
        <Callout info>
          {waiting.map(row => <Typography key={row.project_id} style={{ fontSize: '.85rem' }}>{waitingLine(row)}</Typography>)}
        </Callout>
      </div>
    );
  }
  const enrolled = totals.find(item => item.key === 'enrolled_learners');
  if (enrolled && Number(enrolled.value) === 0) {
    return <div style={{ marginTop: 16 }}><Callout title="No real learner is using Learning Hub yet." /></div>;
  }
  return null;
};

/** `learning_hub` section. */
const LearningHubInsights = ({ data = {}, onProject }) => {
  const classes = useInsightsStyles();
  const plans = data.plans || {};
  const projects = data.projects || [];
  const newEnrollments = data.new_enrollments;
  const testNote = data.test_accounts?.note;
  const planItems = [
    { key: 'projects_on_plan', label: 'Projects on a plan', value: plans.projects_on_plan },
    { key: 'active', label: 'Active', value: plans.active },
    { key: 'scheduled', label: 'Scheduled', value: plans.scheduled },
    { key: 'expired', label: 'Expired', value: plans.expired },
    { key: 'ending', label: 'Ending within 14 days', value: plans.ending_within_14_days, warning: Number(plans.ending_within_14_days) > 0 },
  ];
  const totals = [
    ...(data.totals || []),
    ...(newEnrollments ? [{
      key: 'new_enrollments',
      label: 'New enrolments',
      value: newEnrollments.current,
      note: `${formatCount(newEnrollments.previous)} in the period before`,
      description: newEnrollments.description,
    }] : []),
  ];
  const projectColumns = [
    {
      key: 'project',
      label: 'Project',
      render: row => (
        <>
          <div>{row.project || '-'}</div>
          {Number(row.test_enrollments) > 0 ? <div className={classes.muted} style={{ fontSize: '.72rem' }}>{testEnrollmentsHint(row.test_enrollments)}</div> : null}
        </>
      ),
    },
    { key: 'customer', label: 'Company' },
    { key: 'plan', label: 'Plan', render: row => <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>{row.plan || '-'} <LearningHubBadge value={row.access_state} /></span> },
    { key: 'window', label: 'Access window', render: row => `${formatDate(row.access_start_date)} – ${formatDate(row.access_end_date)}` },
    {
      key: 'days_left',
      label: 'Days left',
      render: row => {
        const endingSoon = row.access_state === 'active' && row.days_left !== null && Number(row.days_left) <= 14;
        return <span style={{ whiteSpace: 'nowrap', ...(endingSoon ? { color: '#b45309', fontWeight: 700 } : {}) }}>{daysLeftText(row)}</span>;
      },
    },
    { key: 'learners_in_project', label: 'In project', render: row => formatCount(row.learners_in_project) },
    { key: 'learners_with_access', label: 'With access', render: row => formatCount(row.learners_with_access) },
    { key: 'enrolled_learners', label: 'Enrolled', render: row => formatCount(row.enrolled_learners) },
    { key: 'learning_now', label: 'Learning now', render: row => formatCount(row.learning_now) },
    { key: 'avg_progress_pct', label: 'Avg progress', render: row => formatPercent(row.avg_progress_pct) },
  ];
  const courseColumns = [
    { key: 'course', label: 'Course' }, { key: 'provider', label: 'Provider' },
    { key: 'learners', label: 'Learners', render: row => formatCount(row.learners) }, { key: 'learning', label: 'Learning', render: row => formatCount(row.learning) },
    { key: 'completed', label: 'Completed', render: row => formatCount(row.completed) }, { key: 'avg_progress_pct', label: 'Avg progress', render: row => formatPercent(row.avg_progress_pct) },
  ];
  return (
    <Section id="learning_hub" title="Learning Hub" subtitle={plans.description} exportKey="learning_hub">
      <Panel>
        <MetricGrid items={planItems} columns={5} />
        <div style={{ marginTop: 16 }}><MetricGrid items={totals} columns={totals.length > 5 ? 3 : 5} /></div>
        {testNote ? <Typography className={classes.footnote} data-testid="learning-hub-test-note">{testNote}</Typography> : null}
        <LearningHubBanner plans={plans} projects={projects} totals={data.totals || []} />
        <Typography component="h3" className={classes.subheading}>Projects with Learning Hub</Typography>
        <DataTable ariaLabel="Projects with Learning Hub" columns={projectColumns} rows={projects} onRowClick={onProject} getRowKey={row => row.project_id} emptyText={plans.description} />
        <Typography component="h3" className={classes.subheading}>Most used Learning Hub courses</Typography>
        <DataTable ariaLabel="Most used Learning Hub courses" columns={courseColumns} rows={data.top_courses || []} getRowKey={row => row.course_id} emptyText={data.note} />
        {data.note ? <Typography className={classes.footnote}>{data.note}</Typography> : null}
      </Panel>
    </Section>
  );
};

export default LearningHubInsights;
